// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { MONTHS, OUTCOME_COLOUR, OUTCOME_LABEL, OUTCOME_ORDER, SEASON_OF_MONTH } from '../domain';
import { monthOutcomeMatrix, monthCounts, periodOfDayCounts } from '../analysis';
import { stackedColumns, horizontalBars, type StackSeg, type StackDatum } from '../components/charts';
import { formatNumber, formatPercent } from '../utils/format';

const SEASONS = ['Summer', 'Autumn', 'Winter', 'Spring'];
const SEASON_COLOUR: Record<string, string> = { Summer: '#e08a1e', Autumn: '#b5651d', Winter: '#2b6f8f', Spring: '#2a9d8f' };
const POD_LABEL: Record<string, string> = { dawn: 'Dawn (5–7am)', day: 'Daytime (7am–5pm)', dusk: 'Dusk (5–8pm)', night: 'Night (8pm–5am)' };
const POD_ORDER = ['dawn', 'day', 'dusk', 'night'];

export function renderSeasonality(root: HTMLElement, ctx: ViewContext): void {
  const counts = monthCounts(ctx.data.incidents);
  const matrix = monthOutcomeMatrix(ctx.data.incidents);
  const pod = periodOfDayCounts(ctx.data.incidents);

  const seasonCounts: Record<string, number> = { Summer: 0, Autumn: 0, Winter: 0, Spring: 0 };
  for (let m = 1; m <= 12; m++) seasonCounts[SEASON_OF_MONTH[m]] += counts[m - 1];
  const totalWithMonth = counts.reduce((a, b) => a + b, 0);
  const podTotal = POD_ORDER.reduce((s, k) => s + pod[k], 0);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">When shark incidents happen</h1>
      <p class="view-sub">
        Incidents follow people into the water. The Australian summer — warm water, school holidays, crowded surf — is
        by far the busiest season, and the middle of the day the busiest time.
      </p>
    </div>

    <div class="stat-grid">
      ${SEASONS.map(
        (s) => `<div class="stat">
          <div class="stat-label"><span class="legend-swatch" style="background:${SEASON_COLOUR[s]}"></span>${s}</div>
          <div class="stat-value">${formatNumber(seasonCounts[s])}</div>
          <div class="stat-note">${formatPercent(totalWithMonth ? (seasonCounts[s] / totalWithMonth) * 100 : 0, 0)} of dated incidents</div>
        </div>`,
      ).join('')}
    </div>

    <div class="panel">
      <div class="panel-title">Incidents by month, by outcome</div>
      <div class="panel-sub">Southern-hemisphere seasons: the December–February peak is unmistakable. Hover any column.</div>
      <div class="chart-scroll" data-role="months"></div>
      <div class="legend" data-role="legend"></div>
    </div>

    <div class="panel" style="margin-top:var(--space-lg)">
      <div class="panel-title">Time of day</div>
      <div class="panel-sub">For the ${formatNumber(podTotal)} incidents with a recorded time. Most happen in daylight, when most people are in the water — not, as myth has it, mainly at dawn and dusk.</div>
      <div class="chart-scroll" data-role="pod"></div>
    </div>
  `;

  const segs: StackSeg[] = OUTCOME_ORDER.map((o) => ({ key: o, colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] }));
  const data: StackDatum[] = MONTHS.map((mLabel, i) => ({
    id: mLabel,
    label: mLabel,
    values: {
      fatal: matrix[i][OUTCOME_ORDER.indexOf('fatal')],
      injured: matrix[i][OUTCOME_ORDER.indexOf('injured')],
      uninjured: matrix[i][OUTCOME_ORDER.indexOf('uninjured')],
      unknown: matrix[i][OUTCOME_ORDER.indexOf('unknown')],
    },
    extraTip: SEASON_OF_MONTH[i + 1],
  }));
  (root.querySelector('[data-role="months"]') as HTMLElement).appendChild(stackedColumns(data, segs, { height: 320, labelEvery: 1 }));
  (root.querySelector('[data-role="legend"]') as HTMLElement).innerHTML = OUTCOME_ORDER.map(
    (o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]}</span>`,
  ).join('');

  (root.querySelector('[data-role="pod"]') as HTMLElement).appendChild(
    horizontalBars(
      POD_ORDER.map((k) => ({
        id: k,
        label: POD_LABEL[k],
        value: pod[k],
        colour: '#2b8994',
        tip: `${POD_LABEL[k]}: ${formatNumber(pod[k])} incidents (${formatPercent(podTotal ? (pod[k] / podTotal) * 100 : 0, 0)})`,
      })),
      { labelWidth: 160 },
    ),
  );
}
